/**
 * Der Abschnitt „Artikel" des Bestell-Reiters - Portierung von `index.html:8479-8506` (Werkzeugzeile,
 * Tabellenhuelle, Legende, „Artikel hinzufügen", Warenwert) samt `renderItems` (`:9789-9818`), das
 * im Bestand die Zeilen NACH dem Zeichnen des Reiters in `#itemRows` nachtraegt.
 *
 * Die Spaltenbreiten kommen aus `ITEM_TABLE_COLUMNS` (`config/board.ts`, index.html:4625) und der
 * arbeitsplatzlokalen Einstellung `hubItemTableWidths` (index.html:4638) - laut Spec bleiben
 * Spaltenbreiten in `localStorage` und wandern nicht in die Datenbank.
 *
 * ================= Verhaltensaenderung 3 (die zweite ausgenommene Flaeche) =================
 *
 * Spec, woertlich: „Artikel und Lieferanten werden nicht mehr frei eingetippt, sondern ueber
 * JTL-Suchfelder ausgewaehlt. Betrifft das Anlegen von Bestellungen, die Positionsbearbeitung und
 * die Lieferantenverwaltung."
 *
 * Fuer die Positionsbearbeitung heisst das: das Artikelfeld ist ein Suchfeld gegen JTL
 * (`src/api/jtlArtikelSuche.ts`, Constraint 16), und die SKU kommt mit dem gewaehlten Artikel statt aus der
 * Tastatur - sie ist deshalb `readOnly`. Ausgewaehlt wird ueber die Trefferliste; sie steht nur,
 * solange gesucht wird, und traegt darum im Ruhezustand KEINEN zusaetzlichen Knoten. Deshalb ist der
 * Strukturvergleich gegen `drawer-main` von dieser Aenderung unberuehrt - siehe
 * `test/komponenten/Drawer/positionenStruktur` im Bericht.
 *
 * Diese Flaeche ist von der Screenshot-Baseline ausgenommen (`test/visual/ausnahmen.ts`, Eintrag
 * `Drawer/MainTab/Positionsbearbeitung`); der Nachweis laeuft ueber
 * `test/komponenten/Drawer/positionen.test.tsx`.
 *
 * NACHTRAG 2026-08-25 (drei Fehler in dieser Zelle - Bericht "Drei Fehler in der Artikelzelle des
 * Bestell-Reiters"): das Feld war zwar ein Suchfeld, schrieb den getippten Text aber nirgends hin -
 * kontrolliert auf `it.name`, setzte React den DOM-Wert nach jedem Tastenanschlag zurueck. Behoben
 * mit demselben `Zeilensuche`-Zustand wie in `NewOrder/PositionsEditor.tsx` (das Feld zeigt den
 * Suchtext, solange gesucht wird). Die Trefferliste haengt `position: absolute` in einer Zelle, die
 * hier in ZWEI beschneidenden Kaesten steckt (`.items-table-scroll` rechnet `overflow-x: auto`
 * implizit auf `overflow-y: auto` um, `.drawer-body` traegt selbst `overflow-y: auto`) - gemessen am
 * 2026-08-15 waren in der baugleichen Anlagemasken-Tabelle 0 von 3 Optionen sichtbar. Die Abhilfe der
 * Anlagemaske (Scrollkasten abschalten, `suchfeld.css`) passt hier NICHT: der Bestell-Reiter hat
 * Spaltentrenner und arbeitsplatzlokale Breiten (`hubItemTableWidths`), sein Scrollkasten ist nicht
 * entbehrlich - die Liste haengt hier stattdessen in Bildschirmkoordinaten (`position: fixed`,
 * Geometrie aus `getBoundingClientRect()`, siehe `TREFFER_ABSTAND` unten). Und ein Suchfehler war
 * eine unbehandelte Zurueckweisung ohne Ablehnungszweig - derselbe Befund wurde am 2026-08-13 in
 * `NewOrder/JtlSuchfeld.tsx` abgestellt.
 *
 * ================= NICHT verdrahtet (gemeldet) =================
 *
 * Das Ziehen von Positionen (`startItemDrag`, `dropItemPosition`, `handleItemReorderKey`,
 * index.html:9700-9788) ist seit H-4 PORTIERT, nur nicht HIER verdrahtet: `NewOrder/
 * PositionenZiehen.ts` liefert die Handler fuer den Griff
 * (`className="item-drag-handle"`, `draggable="true"`) in `PositionsEditor.tsx` (Anlagemaske) und
 * nennt diese Datei ausdruecklich als dieselbe Luecke im Bestell-Reiter (Kopfkommentar dort,
 * Abschnitt "Dieselbe Luecke im Bestell-Reiter"). Derselbe Griff steht auch hier, aber ohne
 * Handler.
 *
 * Das Ziehen der Spaltentrenner (`startItemColumnResize`, `resizeItemColumnByKey`, `:6838-6870`)
 * ist dagegen weiterhin NICHT portiert - nirgends im Baum. Beide Griffe sind gezeichnet, keiner
 * ist hier verdrahtet - dieselbe Grenze, die zunaechst auch fuer `startDrawerResize` galt, bis es
 * in `Drawer.tsx` verdrahtet wurde (siehe dort, Abschnitt "Die Breite"). `Zurücksetzen`
 * (`resetItemTableWidths`, `:6871`) haengt an `onSpaltenbreiten`.
 *
 * NACHTRAG (Auftrag "Irrefuehrende Drag-Tooltips", 2026-09-07): Beide Griffe trugen bis hierher
 * denselben `title` wie ihre verdrahteten Gegenstuecke ("Ziehen oder mit [den] Pfeiltasten
 * ändern/verschieben") - ein Bedienversprechen ohne jeden Handler dahinter. Der Ziehgriff verlor
 * zusaetzlich `draggable="true"`: ohne `onDragStart` loest es nur die native Browser-Ziehanimation
 * aus (Ghost-Bild, kein Effekt) und hat, geprueft gegen `styles/bestand.css`, keinen eigenen
 * `[draggable]`-Selektor - der Cursor `grab`/`grabbing` haengt an der Klasse `.item-drag-handle`.
 * Beide `title`s sind seither neutral ("Position" / "Spaltenbreite") ohne Bedienversprechen; siehe
 * Entscheidung des Auftraggebers dort. `aria-label` bleibt unveraendert (nicht Teil des Befunds).
 */
import { useEffect, useRef, useState } from "react";
import { ITEM_TABLE_COLUMNS } from "../../../config/board.ts";
import { kennungVon } from "../../../lib/bestellkennung.ts";
import { fmtMoney } from "../../../lib/money.ts";
import { orderTotal } from "../../../lib/payments.ts";
import type { JtlArtikel, JtlSuche } from "../../../api/jtlArtikelSuche.ts";
import { geladeneDokumente, POSITIONSDOKUMENTE_LAEDT, type PositionsDokumenteZustand } from "../../../lib/positionsDokumenteZustand.ts";
import { schreibwegVon as positionsSchreibwegAus } from "../../../lib/positionsSchreibweg.ts";
import type { Order, OrderItem, Supplier } from "../../../lib/types.ts";
import { Artikelbild } from "../../Artikelbild.tsx";
import { artikelKennung } from "../../../lib/artikelbild.ts";
import { verknuepftMarke } from "../../VerknuepftMarke.tsx";
import { feldZustandClass } from "../../../utils/feldZustand.ts";
import { fehlertext } from "../../../lib/fehlertext.ts";
import {
  PositionsdokumentAktionen,
  type PositionsDokumentMenueSchluessel,
} from "./PositionsdokumentAktionen.tsx";
import type { ProduktionsbildOeffnen } from "../DocsTab/Positionsdokumente.tsx";
import type { UploadStellen } from "../../App/useUploadStellen.ts";

/** `index.html:4633-4634` - die beiden Spalten ohne Ueberschrift. */
const ITEM_REORDER_COLUMN_WIDTH = 34;
const ITEM_ACTION_COLUMN_WIDTH = 30;
/**
 * Auftrag 2 - dritte kopflose Spalte, nach demselben Muster wie die beiden obigen: das
 * Artikelbild-Thumbnail braucht keine Ueberschrift und ist nicht ueber `ITEM_TABLE_COLUMNS`
 * konfigurierbar (kein Spalteneditor, keine gespeicherte Breite).
 */
const ITEM_IMAGE_COLUMN_WIDTH = 36;

/** Derselbe Abstand unter/ueber dem Feld wie `top: calc(100% + 6px)` der Grundregel (`suchfeld.css`). */
const TREFFER_ABSTAND = 6;
/** `max-height: 320px` der Grundregel in `suchfeld.css` - die groesstmoegliche Hoehe. */
const TREFFER_HOECHSTHOEHE = 320;

/** Die Schwebe-Geometrie der Trefferliste/des Fehlerhinweises. */
interface TrefferGeometrie {
  top: number;
  left: number;
  breite: number;
  hoehe: number;
}

/**
 * Nachbesserung zu c414bbc (Fehler 2, Teil 2): `top = rect.bottom + TREFFER_ABSTAND` allein schiebt
 * eine bis zu `TREFFER_HOECHSTHOEHE` hohe Liste unter den unteren Fensterrand, wenn das Artikelfeld
 * im unteren Streifen des Fensters steht - und ein `position: fixed`-Kasten laesst sich NICHT
 * heranscrollen (sein umschliessender Block ist der Viewport, siehe Kopfkommentar oben). Die Liste
 * muss deshalb umklappen (ueber statt unter das Feld) UND ihre Hoehe auf den Platz begrenzen, der auf
 * der gewaehlten Seite tatsaechlich frei ist.
 */
function geometrieAus(feld: HTMLInputElement): TrefferGeometrie {
  const rect = feld.getBoundingClientRect();
  const platzUnten = window.innerHeight - rect.bottom - TREFFER_ABSTAND;
  const platzOben = rect.top - TREFFER_ABSTAND;
  // Nach unten, solange dort so viel Platz ist wie der Kasten oben ueberhaupt bekaeme - sonst
  // klappt er ueber das Feld.
  const nachUnten = platzUnten >= Math.min(TREFFER_HOECHSTHOEHE, platzOben);
  const hoehe = Math.min(TREFFER_HOECHSTHOEHE, Math.max(0, nachUnten ? platzUnten : platzOben));
  return {
    top: nachUnten
      ? rect.bottom + TREFFER_ABSTAND
      : Math.max(0, rect.top - TREFFER_ABSTAND - hoehe),
    left: rect.left,
    breite: rect.width,
    hoehe,
  };
}

/** Vergleicht zwei Geometrien auf Wertgleichheit (C3): vermeidet unnoetige State-Schreibvorgaenge in
 *  `geometrieNachfuehren`, wenn sich beim Scrollen tatsaechlich nichts veraendert hat - sonst zeichnet
 *  JEDES Scroll-Ereignis im Dokument die ganze Positionstabelle neu, auch das Scrollen IN der
 *  Trefferliste selbst (die hat `overflow-y: auto`, `suchfeld.css`), wo sich nichts bewegt hat. */
function geometrieGleich(a: TrefferGeometrie | null, b: TrefferGeometrie): boolean {
  return !!a && a.top === b.top && a.left === b.left && a.breite === b.breite && a.hoehe === b.hoehe;
}

/**
 * `icon(name, className)` (`index.html:4016`) samt der Nachbearbeitung durch `hydrateIcons`
 * (`:4021`) - die gemeinsame Registratur zeichnet das fertige `<svg class="lucide lucide-…">`.
 *
 * Bis `feat/09-spa-ikonen` stand hier eine LOKALE Funktion, die nur den Platzhalter
 * `<i data-lucide="…">` erzeugte. Den hydriert in der SPA niemand (die UMD-Fassung laeuft allein
 * im Baseline-Rundlauf des Playwright-Harness) - es blieb eine leere Flaeche. Siehe `Ikone.tsx`.
 */
import { Ikone } from "../../Ikone.tsx";

/** `index.html:6790` */
function itemTableColumnWidth(
  spalte: (typeof ITEM_TABLE_COLUMNS)[number],
  breiten: Record<string, number>,
): number {
  return Math.max(spalte.min, Math.min(spalte.max, Number(breiten[spalte.key]) || spalte.width));
}

/** `index.html:6794` */
function itemTableWidth(breiten: Record<string, number>): number {
  return (
    ITEM_TABLE_COLUMNS.reduce((summe, spalte) => summe + itemTableColumnWidth(spalte, breiten), 0) +
    ITEM_REORDER_COLUMN_WIDTH +
    ITEM_IMAGE_COLUMN_WIDTH +
    ITEM_ACTION_COLUMN_WIDTH
  );
}

/** `index.html:7833` - der leere Positionsdatensatz, woertlich. */
export function blankOrderItem(): OrderItem {
  return {
    name: "",
    sku: "",
    ean: "",
    qty: "",
    price: "",
    foc: false,
    focQty: "",
    manual: false,
    supplierInvoice: "",
    customsTariff: "",
    customsGroupAmount: "",
    customsInvoice: "",
    dutyPctJtl: "",
    freightInvoice: "",
    toolingCost: "",
    usdRate: "",
    mixedUsdRate: "",
    qtyEffOverride: "",
    ekDeOverride: "",
    ekNewOverride: "",
  } as OrderItem;
}

export interface PositionenProps {
  bestellung: Order;
  lieferanten: readonly Supplier[];
  onAendern: (id: string, aenderung: Record<string, unknown>) => void;
  /**
   * `ITEM_TABLE_WIDTHS` aus `localStorage` (`hubItemTableWidths`) - vom Aufrufer gehalten, siehe
   * unten. Fehlt sie, bleibt es bei den Vorgabebreiten aus `ITEM_TABLE_COLUMNS`.
   */
  artikelBreiten?: Record<string, unknown>;
  onSpaltenbreiten?: () => void;
  jtlArtikelSuche?: JtlSuche<JtlArtikel>;
  /**
   * Schreibt EINE Position ueber `PATCH /api/orders/:id/items/:jtlPosId` (`api/positionen.ts`).
   * Getrennt von `onAendern`, weil es eine andere Ressource mit einer EIGENEN `rowVersion` ist -
   * wortgleich zu `CalcTab/EkTab.tsx#onPositionAendern`, und derselbe Rueckruf dahinter
   * (`App.tsx#drawerPositionAendern`).
   *
   * Optional, damit die vorhandenen Tests, die diese Komponente ohne den Rueckruf zeichnen,
   * weiterhin den ALTEN (lauten) Weg messen statt still etwas anderes zu belegen.
   *
   * `sortIndex` (Task 11): der Laufindex der Position in `positionen` - dieselbe Groesse, die
   * `positionszeilenAus` (`api/positionen.ts`) beim Anlegen mehrerer Zeilen auf einmal verwendet.
   * Wird bei JEDEM Aufruf mitgegeben, nicht nur beim ersten: `api/positionen.ts#positionAendern`
   * dokumentiert, dass der Server ihn ignoriert, sobald die Zeile schon existiert - diese Komponente
   * muss also nicht selbst unterscheiden, ob es der erste Schreibvorgang auf diese Position ist.
   */
  onPositionAendern?: (
    jtlPosId: number,
    aenderung: Record<string, unknown>,
    rowVersion: number,
    sortIndex: number,
  ) => void;
  /**
   * Schreibt kArtikel/cArtNr/cName EINER bestehenden Position ueber den neuen, positions-scoped
   * JTL-Endpunkt (`api/jtlBestellungen.ts#jtlPositionArtikelTauschen`) - der Artikeltausch, siehe
   * docs/superpowers/specs/2026-09-01-artikeltausch-bestehende-position-design.md. Getrennt von
   * `onPositionAendern`: `name`/`sku` sind JTL-Felder (kein order_item_extras-Schreibweg,
   * `api/positionen.ts#POSITIONS_FELDER` kennt sie nicht), `ean` bleibt dagegen ein Tool-Feld und
   * geht weiterhin ueber `onPositionAendern`.
   *
   * Optional aus demselben Grund wie `onPositionAendern`: vorhandene Tests ohne diesen Rueckruf
   * sollen weiterhin den lokalen Weg (`onAendern`) messen.
   */
  onArtikelTauschen?: (
    jtlPosId: number,
    aenderung: { kArtikel: number; cArtNr: string; cName: string | null },
  ) => void;
  /**
   * Schreibt Menge/Preis EINER bestehenden Position ueber den positions-scoped JTL-Endpunkt
   * (`api/jtlBestellungen.ts#jtlPositionMengePreisAendern`). Design: docs/superpowers/specs/
   * 2026-09-04-positionen-crud-design.md. Optional aus demselben Grund wie `onArtikelTauschen`.
   */
  onMengePreisAendern?: (
    jtlPosId: number,
    aenderung: { qty?: number; price?: number },
  ) => void;
  /**
   * Loescht EINE bestehende Position ueber den positions-scoped JTL-Endpunkt
   * (`api/jtlBestellungen.ts#jtlPositionLoeschen`). Design: docs/superpowers/specs/
   * 2026-09-04-positionen-crud-design.md. Optional aus demselben Grund wie `onArtikelTauschen`.
   */
  onPositionLoeschen?: (jtlPosId: number) => void;
  /**
   * Legt eine NEUE Position an ueber den positions-scoped JTL-Endpunkt
   * (`api/jtlBestellungen.ts#jtlPositionHinzufuegen`). Design: docs/superpowers/specs/
   * 2026-09-04-positionen-crud-design.md. Ausgeloest per Blur des Mengenfelds einer neuen Zeile,
   * sobald ein Artikel-Kandidat UND eine Menge > 0 vorliegen (siehe `uebernehmen`/`NeueZeile.kandidat`).
   * Optional aus demselben Grund wie `onArtikelTauschen`: vorhandene Tests ohne diesen Rueckruf sollen
   * unveraendert bleiben (dann bleibt die neue Zeile einfach lokal leer, wie im Bestand).
   */
  onPositionHinzufuegen?: (neuePosition: {
    kArtikel: number;
    cArtNr: string;
    cName: string | null;
    qty: number;
    price: number;
    mwstProzent: number;
  }) => void;
  /**
   * Wird von den bedienbaren Positionsdokument-Aktionen im Grid ausgewertet. Drei Ladezustaende (Fehleranzeige Paket 2, Task 13, Spec E4):
   * nur mit geladenem Stand tragen die Zellen Upload-Knoepfe, sonst steht dort „nicht geladen“ (gescheitert; die Flaeche mit Grund und
   * „Erneut laden“ steht im Reiter Dokumente) bzw. „Lädt…“. Ohne Angabe gilt `laedt`.
   */
  positionsDokumenteZustand?: PositionsDokumenteZustand;
  /** Gemeinsamer Nachladeweg des offenen Drawers, genutzt von der Positionsdokumentverwaltung. */
  positionsDokumenteNeuLaden?: () => Promise<void>;
  /** Öffnet die gemeinsame Lightbox am im Grid gewählten Produktionsbild. */
  onProduktionsbildOeffnen?: ProduktionsbildOeffnen;
  /** Bestehender Badge-Einstieg für Einbettungen ohne Lightbox-Callback. */
  onProduktionsbilderOeffnen?: (jtlPosId: number) => void;
  /**
   * `App.tsx#zeigeHinweis`: ein Upload der Positionsdokument-Aktionen, der scheitert, nachdem diese
   * Bestellung nicht mehr offen ist, geht als stehender Hinweis hinaus (Task 6, "Uploads bis 100 MB").
   */
  onStehenderHinweis?: (text: string) => void;
  /**
   * `App.tsx#uploadStellen` (ueber `BestellungTab`): der Store der Upload-Stellen. Die Positionsdokument-Aktionen
   * sperren darin je Position (Spec 2026-10-20, Luecke 5) und teilen sich die Sperre mit dem Reiter Dokumente. Ohne
   * Prop legt jede Zeile sich einen eigenen Store an - die Sperre gilt dann nur fuer diese Zeile.
   */
  uploadStellen?: UploadStellen;
}

/**
 * Die offene Trefferliste GENAU EINER Zeile - samt Suchtext und Fehler. `null` = keine offen.
 * Dieselbe Bauform wie `NewOrder/PositionsEditor.tsx#Zeilensuche`.
 */
interface Zeilensuche {
  zeile: number;
  /**
   * Der getippte Text - er steht im Feld ANSTELLE des Artikelnamens, solange gesucht wird. Ohne ihn
   * waere das Feld ein kontrolliertes `value={it.name}`, in dem Tippen nichts bewegt (Fehler 1 des
   * Berichts vom 2026-08-25: React setzte den DOM-Wert nach jedem Tastenanschlag zurueck).
   */
  text: string;
  liste: JtlArtikel[];
  fehler: string | null;
}

/**
 * Eine rein lokale, noch nicht in JTL angelegte Positionszeile (siehe `neueZeilen` unten). Traegt den
 * numerischen Artikel-Kandidaten (falls per Suche gewaehlt) DIREKT am Zeilenobjekt.
 *
 * Kontroll-Sitzung 2026-09-04, Nachpruefung von Runde 2 (kritischer Befund): eine SEPARATE,
 * index-geschluesselte Map (`neuePositionKandidaten`, Runde 2) geriet aus dem Takt, sobald Punkt 6
 * (die Zeile wird bei erfolgreicher Anlage sofort aus `neueZeilen` entfernt) NICHT die letzte lokale
 * Zeile traf: alle dahinterliegenden lokalen Zeilen ruecken im kombinierten Index eins nach vorne,
 * die Map wurde aber nur um den EINEN abgesendeten Schluessel erleichtert, nie umgeschluesselt - ein
 * Kandidat konnte so an der FALSCHEN (nachgerueckten) Zeile haengen bleiben. Der Kandidat direkt am
 * Objekt macht das strukturell unmoeglich: er wandert IMMER mit der Zeile mit, egal an welchem Index
 * sie gerade im Array steht, und verschwindet automatisch MIT ihr (Filter/Splice), ohne dass ein
 * separater Reset an einer dritten Stelle noetig waere.
 */
interface NeueZeile extends OrderItem {
  kandidat?: { kArtikel: number; cArtNr: string; cName: string; mwstProzent: number };
  /**
   * Fix-Welle nach der finalen Zweig-Pruefung (2026-09-04, I3): `true`, wenn der zuletzt gewaehlte
   * Treffer AUS DIESEM EINEN GRUND keinen Kandidaten ergab - `kArtikel`/`cName` waren gueltig, nur
   * `mwstProzent` war kein lesbarer Wert (`sucheArtikel` liefert `null`, wenn der Artikel beim
   * GEWAEHLTEN Lieferanten nicht gepflegt ist, `backend/src/jtl/artikel.ts` - derselbe LEFT JOIN
   * gegen `tliefartikel`, der auch `verknuepft` speist). VORHER blieb das eine stumme Sackgasse:
   * Menge eintragen, Feld verlassen, buchstaeblich nichts passiert - kein Toast, keine
   * Konsolenmeldung, kein Hinweis. Getrennt von der bestehenden "Nicht verknuepft"-Marke
   * (`verknuepfungen`/`verknuepftMarke`, siehe dort): `verknuepft`/`mwstProzent` kommen zwar aus
   * DERSELBEN Abfrage, sind aber ZWEI verschiedene Spalten derselben (oder auch KEINER) Zeile - ein
   * Artikel kann `verknuepft: true` tragen (die `tliefartikel`-Zeile existiert) und TROTZDEM
   * `mwstProzent: null` haben (der Steuersatz ist in genau dieser Zeile nicht gepflegt). Die
   * bestehende Marke zeigt in diesem Fall "Verknüpft" - fälschlich beruhigend - und würde diesen
   * Fall NICHT anzeigen. Deshalb ein EIGENES Flag statt Wiederverwendung von `verknuepfungen`,
   * aber derselbe VISUELLE Mechanismus (Tooltip-Marke neben dem Artikelfeld, siehe unten).
   *
   * Wird bei JEDER Artikelwahl fuer eine lokale Zeile explizit gesetzt ODER zurueckgesetzt (nie nur
   * einseitig geschrieben) - sonst bliebe eine alte Warnung stehen, nachdem der Bediener einen
   * zweiten, diesmal gueltigen Artikel gewaehlt hat.
   */
  steuersatzFehlt?: boolean;
}

export function Positionen({
  bestellung: o,
  lieferanten,
  onAendern,
  artikelBreiten,
  onSpaltenbreiten,
  jtlArtikelSuche,
  onPositionAendern,
  onArtikelTauschen,
  onMengePreisAendern,
  onPositionLoeschen,
  onPositionHinzufuegen,
  positionsDokumenteZustand = POSITIONSDOKUMENTE_LAEDT,
  onProduktionsbildOeffnen,
  onProduktionsbilderOeffnen,
  positionsDokumenteNeuLaden,
  onStehenderHinweis,
  uploadStellen,
}: PositionenProps) {
  const id = o.id as string;
  const positionen = ((o.items || []) as OrderItem[]);
  const [offenesPositionsdokumentMenue, setOffenesPositionsdokumentMenue] =
    useState<PositionsDokumentMenueSchluessel | null>(null);
  const positionsschluessel = positionen
    .map((position, index) => positionsSchreibwegAus(positionen, index)?.jtlPosId ?? "neu")
    .join(",");

  useEffect(() => {
    setOffenesPositionsdokumentMenue(null);
  }, [id, positionsschluessel]);

  // ARBEITSPLATZEINSTELLUNG, seit dem 2026-08-15 als Prop statt aus einer eigenen
  // `localStorage`-Klammer hier (siehe `state/arbeitsplatz.ts`, "DER FUENFTE"): der Aufrufer haelt
  // sie, damit `onSpaltenbreiten` das Zuruecksetzen SICHTBAR machen kann. Vorher las diese
  // Komponente den Wert einmalig beim Einhaengen - ein Zuruecksetzen von aussen kam nie an.
  const breiten = (artikelBreiten ?? {}) as Record<string, number>;

  /** Zustand der offenen Trefferliste (`Zeilensuche` oben), `null` solange keine Zeile sucht. */
  const [suche, setSuche] = useState<Zeilensuche | null>(null);
  /** Dieselbe Bauform wie `KontextMenue.tsx:94` (`offen = bestellung != null`) - C4. */
  const sucheOffen = suche !== null;

  /** Verknuepfungsstand je Zeilenindex, NUR fuer die Anzeige DIESER Sitzung - darf nie in ein
   *  Aenderungsobjekt wandern, `verknuepft` ist kein Positionsfeld (siehe `uebernehmen` unten).
   *  Zurueckgesetzt bei jedem Loeschen (Indexverschiebung, wie `setSuche(null)` dort - noch kein
   *  Umsortieren verdrahtet, siehe Kopfkommentar der Datei, "NICHT verdrahtet"). */
  const [verknuepfungen, setVerknuepfungen] = useState<Record<number, boolean | null>>({});

  /**
   * Kontroll-Sitzung 2026-09-04 (Pruefer-Befund I-3, groesster Fund der Fix-Runde): rein LOKALE,
   * noch nicht in JTL angelegte Positionszeilen. `positionenSetzen`/`onAendern` scheitert fuer eine
   * voellig neue Zeile in der echten Anwendung IMMER mit `FeldOhneGruppeFehler` (RIEGEL 2, siehe
   * Kopfkommentar `positionenSetzen` unten - "items" steht in KEINER Feldgruppe) - der Button
   * „Artikel hinzufügen" darf eine neue Zeile deshalb NICHT MEHR ueber `onAendern` anlegen, sondern
   * ausschliesslich hier lokal. Bei der Anzeige wird diese Liste an `positionen` (aus der Prop)
   * ANGEHAENGT (siehe `alleZeilen` unten, vor dem Render) - der kombinierte Index bleibt dabei fuer
   * die props-abgeleiteten Zeilen unveraendert (0..positionen.length-1), lokale neue Zeilen bekommen
   * Indizes DARUEBER hinaus. `schreibwegVon`/`positionsSchreibwegAus` liest weiterhin ausschliesslich
   * `positionen` (die Prop) und liefert fuer einen Index jenseits davon automatisch `null` (kein
   * Eintrag im Array) - eine lokale Zeile hat also nie einen Schreibweg, ohne dass diese Datei das
   * gesondert pruefen muesste. `positionPatchen` unten routet einen Index jenseits `positionen.length`
   * direkt in diesen State (kein `onAendern`, kein Netzaufruf - vgl. das Umleitungsprinzip von
   * `onMengePreisAendern`/`onPositionLoeschen` aus Task 5, hier aber rein lokal).
   *
   * Der numerische Artikel-Kandidat (falls per Suche gewaehlt, siehe `uebernehmen` unten) haengt
   * DIREKT am jeweiligen Zeilenobjekt (`NeueZeile.kandidat` oben) - NICHT (mehr) in einer separaten,
   * index-geschluesselten Map, siehe Kopfkommentar `NeueZeile` fuer den Grund (Nachpruefung Runde 2).
   */
  const [neueZeilen, setNeueZeilen] = useState<NeueZeile[]>([]);

  /**
   * Kontroll-Sitzung 2026-09-04, Fix-Runde nach Pruefung (Critical-Befund): lokales Entwurfs-Echo
   * fuer Menge/Preis EINER Position MIT JTL-Schreibweg (`weg && onMengePreisAendern`, siehe
   * `alleZeilen.map` unten). VORHER schrieb jeder Tastenanschlag ueber `positionPatchen` SOFORT
   * gegen die PRODUKTIVE WaWi (`onMengePreisAendern` -> `App.tsx#drawerMengePreisAendern` ->
   * `jtlPositionMengePreisAendern`) - "250" eintippen (Feld leeren, dann 2, 5, 0) waren DREI
   * Schreibvorgaenge gegen ein produktives ERP; dazu ein zweiter Fehler: das Feld ist kontrolliert an
   * `it.qty`/`it.price` (die Prop) gebunden, `positionPatchen` spiegelte bei vorhandenem Schreibweg
   * aber NICHTS lokal - eine (verzoegerte) Serverantwort waehrend des Tippens ueberschrieb das ganze
   * `items`-Array ueber `zusammenfuehren` und konnte das Feld auf einen FRUEHEREN Zwischenwert
   * zurueckspringen lassen, mitten im Tippen.
   *
   * Behoben nach demselben Prinzip wie das Notizfeld in `BestellungTab.tsx` ("erst BEIM VERLASSEN
   * des Feldes, nicht mehr bei jedem Anschlag"): `onInput` schreibt NUR noch hierher
   * (`entwurfAendern`, kein Netzaufruf), der angezeigte Feldwert liest bevorzugt aus diesem Entwurf,
   * `onBlur` (`entwurfCommitten` unten) loest GENAU EINEN Schreibvorgang aus - und nur, wenn sich der
   * Wert gegenueber dem zuletzt bekannten Prop-Stand tatsaechlich veraendert hat (numerischer
   * Vergleich: `positionen[i].qty`/`.price` kommen aus einem frischen JTL-Merge als ZAHL,
   * `api/jtlBestellungen.ts#alsOrderItem`, der Entwurf ist immer die getippte ZEICHENKETTE - ein
   * String-Vergleich wuerde nach dem ERSTEN Roundtrip IMMER "veraendert" melden).
   *
   * Nachpruefung 2026-09-04 (Wichtig-Befund, Fix-Runde 2): GESCHLUESSELT UEBER `weg.jtlPosId`, NICHT
   * (mehr) ueber den Zeilenindex `i` - dieselbe Fehlerklasse wie der in Task 6 behobene Kandidaten-
   * Bug (`NeueZeile.kandidat`, siehe dort). Repro des Pruefers: Zeilen [P0,P1,P2,P3], Klick auf den
   * Papierkorb von P0 (Loeschvorgang laeuft an, Antwort steht noch aus), Bediener tippt VOR der
   * Antwort in das Mengenfeld von P2 (Index 2) - mit einem index-geschluesselten Entwurf wuerde ein
   * Eintrag unter `2` entstehen. Trifft die Antwort ein, schrumpft `positionen` auf [P1,P2,P3] -
   * Index 2 zeigt jetzt auf P3, nicht mehr auf P2. React haelt den fokussierten Knoten (damals kein
   * stabiler `key`: die Zeilen trugen `key={i}`) - der Entwurf bliebe sichtbar und wuerde beim Blur ueber
   * `positionPatchen(2, ...)` auf **P3.jtlPosId** committet, nicht auf die tatsaechlich bearbeitete
   * Zeile. [Seit den Upload-Sperren (Spec 2026-10-20, Luecke 5) tragen Zeilen mit Kennung den Key
   * `pos-<jtlPosId>`: der Knoten wandert dort mit SEINER Zeile, die Verwechslung kann so nicht mehr
   * entstehen. Der Entwurf bleibt trotzdem an der `jtlPosId` geschluesselt - er muss auch ueber eine
   * Neuzeichnung mit anderem Index hinweg zur selben Zeile gehoeren, und eine Zeile ohne Kennung hat
   * ohnehin keinen Entwurf.] `weg.jtlPosId` ist STABIL: jede Zeile mit einem Entwurf hat per Konstruktion
   * einen Schreibweg (`weg && onMengePreisAendern`, siehe die Bedingung an `entwurfAendern` unten)
   * und damit eine `jtlPosId`, die von einer Indexverschiebung durch Loeschen einer ANDEREN Zeile
   * nicht betroffen ist - `kLieferantenBestellungPos` ist der JTL-Primaerschluessel der Position
   * (`backend/src/jtl/typen.ts#JtlBestellPosition`), nicht per Bestellung neu vergeben.
   *
   * GERADE DESHALB braucht dieser State - anders als `verknuepfungen`/`neueZeilen` - KEINEN Reset an
   * den beiden ueblichen Stellen (Bestellungswechsel, Loeschen-Handler): eine geloeschte Zeile oder
   * ein Bestellungswechsel hinterlassen hoechstens einen verwaisten Eintrag unter einer `jtlPosId`,
   * die keine aktuelle Zeile mehr traegt - der wird nie wieder gelesen (kein Schluesselzugriff
   * `mengePreisEntwuerfe[weg.jtlPosId]` kann ihn treffen, ohne dass eine Zeile mit GENAU dieser
   * `jtlPosId` existiert) und ist damit reiner, folgenloser Datenmuell, kein Korrektheitsproblem.
   *
   * Betrifft NUR Zeilen MIT Schreibweg: der rein lokale Weg (`neueZeilen`, `i >= positionen.length`)
   * und der alte Hub-Rueckfall (kein Schreibweg / `onMengePreisAendern` fehlt) bleiben unveraendert
   * bei sofortigem `onInput` ueber `positionPatchen` - dort besteht kein Betriebsrisiko (lokaler
   * State bzw. Hub-Overlay, nicht JTL).
   *
   * GEMELDET, nicht heimlich weggelassen: `entwurfCommitten` loescht den Entwurf des Feldes SOFORT
   * beim Verlassen (ob geschrieben wurde oder nicht), statt ihn bis zur Serverantwort stehen zu
   * lassen - einfachster Kompromiss dieser Fix-Runde, der ein DAUERHAFTES Verdecken einer echten
   * Fremdaenderung ausschliesst (JTL ist fuehrend). Preis: das Feld kann fuer den einen Roundtrip
   * zwischen Blur und Serverantwort kurz auf den (dann schon ueberholten) Prop-Wert zurueckfallen,
   * bevor die Antwort den neuen Stand liefert - kein Datenverlust, nur ein kurzer Anzeigeversatz, und
   * anders als der urspruengliche Befund NIE waehrend des Tippens (das Feld hat den Fokus zu diesem
   * Zeitpunkt bereits verlassen).
   */
  const [mengePreisEntwuerfe, setMengePreisEntwuerfe] = useState<Record<number, { qty?: string; price?: string }>>(
    {},
  );

  /**
   * Pruefungsbefund (2026-09-01, "Wichtig"): `<Drawer>` traegt kein `key={id}` (`App.tsx`,
   * `drawerOeffnen` tauscht nur die Kennung) - diese Komponente bleibt beim Wechsel auf eine ANDERE
   * Bestellung im selben Drawer dieselbe Instanz, `verknuepfungen` also unveraendert stehen. Ohne
   * diesen Wächter traegt Zeile 1 der NEUEN Bestellung die rote Marke der ALTEN weiter, wenn dort
   * gerade eine unverknuepfte Position gewaehlt war - dieselbe Klasse Fehler, die `lieferantWaehlen`
   * in `NewOrder/Anlagemaske.tsx` fuer den Lieferantenwechsel behebt, hier fuer den Bestellungswechsel.
   * React-Standardmuster "State beim Prop-Wechsel anpassen" (Reset WAEHREND des Renderns, kein
   * Effekt - vermeidet den einen Render mit noch altem Stand, den ein `useEffect` haette).
   *
   * Kontroll-Sitzung 2026-09-04 (Pruefer-Befund I-1): `neueZeilen` haengt GENAUSO an einem Index
   * dieser Instanz wie `verknuepfungen` - ohne denselben Reset wuerde eine halbfertige neue Zeile
   * (samt ihrem am Objekt haengenden Kandidaten, siehe `NeueZeile` oben) von der ALTEN Bestellung an
   * der NEUEN weiter haengen. Da der Kandidat seit der Nachpruefung von Runde 2 direkt am
   * Zeilenobjekt haengt (nicht mehr in einer eigenen Map), reicht der Reset von `neueZeilen` allein -
   * es gibt keinen separaten, index-abhaengigen Kandidaten-State mehr, der zusaetzlich zurueckgesetzt
   * werden muesste.
   *
   * Fix-Runde 2026-09-04, Runde 2: `mengePreisEntwuerfe` braucht HIER (anders als `verknuepfungen`/
   * `neueZeilen`) KEINEN Reset - es haengt seit der Nachpruefung an der stabilen `jtlPosId` statt am
   * Zeilenindex, siehe dessen Kopfkommentar fuer den vollstaendigen Grund.
   */
  const [vorigeId, setVorigeId] = useState(id);
  if (vorigeId !== id) {
    setVorigeId(id);
    if (Object.keys(verknuepfungen).length > 0) setVerknuepfungen({});
    if (neueZeilen.length > 0) setNeueZeilen([]);
  }

  /**
   * Schwebeposition der Trefferliste/des Fehlerhinweises (Fehler 2 des Berichts vom 2026-08-25):
   * zwei beschneidende Kaesten - `.items-table-scroll` rechnet `overflow-x: auto` implizit auf
   * `overflow-y: auto` um, `.drawer-body` traegt selbst `overflow-y: auto` - schneiden eine absolut
   * positionierte Liste ab (gemessen am 2026-08-15: 0 von 3 Optionen sichtbar). `position: fixed`
   * entkommt beiden, weil sein umschliessender Block der Viewport ist: `#drawer` traegt zwar
   * `transform: translateX(102%)`, was einen umschliessenden Block ERZEUGEN wuerde, aber im
   * geoeffneten Zustand gilt `#drawer.open { transform: none }` - offen ist der Drawer immer, wenn
   * diese Liste stehen kann.
   */
  const artikelfeldRef = useRef<HTMLInputElement | null>(null);
  const [geometrie, setGeometrie] = useState<TrefferGeometrie | null>(null);

  /** Berechnet die Schwebeposition aus dem aktuell suchenden Feld neu - schreibt (C3) nur, wenn sich
   *  wirklich ein Wert geaendert hat, siehe `geometrieGleich` oben. */
  const geometrieNachfuehren = (): void => {
    const feld = artikelfeldRef.current;
    if (!feld) return;
    const neu = geometrieAus(feld);
    setGeometrie((alt) => (geometrieGleich(alt, neu) ? alt : neu));
  };

  // Nachfuehrung nur, solange eine Suche offen ist - und nur dafuer angemeldet: Scroll-Ereignisse
  // steigen nicht auf, der Horcher muss deshalb in der EINFANGPHASE auf `window` liegen.
  useEffect(() => {
    if (!sucheOffen) return;
    window.addEventListener("scroll", geometrieNachfuehren, true);
    window.addEventListener("resize", geometrieNachfuehren);
    return () => {
      window.removeEventListener("scroll", geometrieNachfuehren, true);
      window.removeEventListener("resize", geometrieNachfuehren);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sucheOffen]);

  /** Die Schwebe-Geometrie als Inline-Style - `right: "auto"` MUSS mitgesetzt werden, die
   *  Grundregel in `suchfeld.css` setzt sonst `right: 0`. `maxHeight` ueberschreibt die
   *  `max-height: 320px` der Grundregel nach UNTEN, nie nach oben (`geometrieAus` klemmt `hoehe`
   *  bereits auf `TREFFER_HOECHSTHOEHE`) - der Kasten scrollt dann intern weiter, das kann er schon
   *  (`overflow-y: auto` in `suchfeld.css`). */
  const schwebeStil = geometrie
    ? ({
        position: "fixed",
        top: `${geometrie.top}px`,
        left: `${geometrie.left}px`,
        width: `${geometrie.breite}px`,
        maxHeight: `${geometrie.hoehe}px`,
        right: "auto",
      } as React.CSSProperties)
    : undefined;

  /**
   * Die vollstaendige Liste zurueckschreiben. Der Bestand mutiert stattdessen `o.items[i][key]` an
   * Ort und Stelle (`patchItem`, index.html:9819) - in React waere das eine Aenderung am Store
   * vorbei, ohne Neuzeichnen.
   *
   * BENANNTE LUECKE, laut statt still, und seit dem 2026-08-15 nur noch der RUECKFALL: `"items"`
   * steht in KEINER Feldgruppe (`api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG`), RIEGEL 2 laesst
   * `bestellungAendern` deshalb mit `FeldOhneGruppeFehler` scheitern, BEVOR ueberhaupt etwas
   * gesendet wird. Der Fehlschlag ist LAUT (Konsole + Toast in `App.tsx#drawerAendern`) und wird
   * nicht umgangen - er trifft jetzt nur noch das LOESCHEN einer BESTEHENDEN Position ohne
   * `onPositionLoeschen` (der Papierkorb unten, alter Rueckfall fuer Zeilen ohne JTL-Kennung).
   *
   * Kontroll-Sitzung 2026-09-04 (Pruefer-Befund I-3): das ANLEGEN einer voellig neuen Zeile (der
   * Knopf „Artikel hinzufügen") ging BIS HIERHIN genau denselben (kaputten) Weg - `positionenSetzen`
   * mit einer laenger gewordenen `items`-Liste, die `FeldOhneGruppeFehler` warf, BEVOR ueberhaupt
   * etwas gesendet wurde. In der echten Anwendung erschien die neue Zeile deshalb NIE, nur ein
   * Fehler-Toast. Der Knopf erzeugt eine neue Zeile jetzt REIN LOKAL (`neueZeilen` oben) und ruft
   * diese Funktion dafuer nicht mehr auf.
   *
   * SEIT TASK 11 NICHT MEHR HIER: „Positionen, die ihre jtlPosId noch nicht tragen" - das war bis
   * dahin der eigentliche FOC-Vorbelegungsfall (`jtlBestellungen.ts#alsOrderItem`, Kopfkommentar
   * "DER VORSCHLAG IST DERZEIT NICHT WIDERRUFBAR") und traf damit ausgerechnet die Bestellungen, bei
   * denen das Abwaehlen ueberhaupt erst gebraucht wird. `schreibwegVon` unten kennt jetzt eine
   * zweite Quelle fuer die Positionskennung (`kLieferantenBestellungPos`, IMMER aus JTL vorhanden)
   * und braucht die Hub-Zeile dafuer nicht mehr.
   */
  const positionenSetzen = (neu: OrderItem[]): void => onAendern(id, { items: neu });

  /**
   * Der Schreibweg EINER Position, wenn sie ihn traegt - sonst `null`. Die eigentliche Ableitung
   * (zwei Quellen fuer die Positionskennung, Sentinel `0` fuer eine unbekannte `rowVersion`) steht
   * seit Task 12 GEMEINSAM mit `EkTab.tsx#positionPatchen` in `lib/positionsSchreibweg.ts` - siehe
   * dort fuer die vollstaendige Begruendung. Hier bleibt nur die diesem Reiter EIGENE Pruefung: ohne
   * `onPositionAendern` gibt es fuer diese Komponente keinen Schreibweg, selbst wenn die Position
   * eine Kennung traegt (siehe `flagSetzen` unten, das denselben Aufruf fuer den Preis-Sonderfall
   * braucht).
   */
  const schreibwegVon = (i: number): { jtlPosId: number; rowVersion: number } | null => {
    if (!onPositionAendern) return null;
    return positionsSchreibwegAus(positionen, i);
  };

  /** `patchItem` (index.html:9819) - ueber die Position, wo es geht, sonst ueber die Bestellung.
   *  `i` reist als `sortIndex` mit (Task 11) - siehe `PositionenProps.onPositionAendern` oben.
   *
   *  Task 5: Menge/Preis gehen bei vorhandenem Schreibweg NICHT ueber `onPositionAendern`
   *  (`order_item_extras`), sondern ueber den EIGENEN, positions-scoped JTL-Endpunkt
   *  `onMengePreisAendern` - `qty`/`price` liegen in JTL, nicht in `POSITIONS_FELDER`
   *  (siehe Kopfkommentar `flagSetzen` unten). Dieser Zweig greift deshalb VOR dem bestehenden
   *  `onPositionAendern`-Zweig. */
  const positionPatchen = (i: number, aenderung: Partial<OrderItem>): void => {
    if (i >= positionen.length) {
      // Kontroll-Sitzung 2026-09-04 (I-3): ein Index jenseits der props-abgeleiteten Positionen
      // zeigt auf eine rein lokale, noch nicht in JTL angelegte Zeile (siehe `neueZeilen` oben) -
      // dafuer gibt es keinen Schreibweg und keine Route, die Aenderung bleibt deshalb IMMER lokal,
      // ohne `onAendern`/`onPositionAendern`/`onMengePreisAendern`.
      const lokalerIndex = i - positionen.length;
      setNeueZeilen((z) => z.map((p, j) => (j === lokalerIndex ? { ...p, ...aenderung } : p)));
      return;
    }
    const weg = schreibwegVon(i);
    if (weg && onMengePreisAendern && ("qty" in aenderung || "price" in aenderung)) {
      // Pruefer-Befund I-2: ein leeres Eingabefeld (`value === ""`, z.B. waehrend der Bediener die
      // Zahl mit Ruecktaste loescht, um sie neu einzutippen) darf NICHT als `0` nach JTL geschrieben
      // werden - `Number("")` ist `0`, und das Backend liesse den Schreibvorgang anstandslos durch
      // (`fMenge`/der EK stuenden kurzzeitig auf 0). Einfach ueberspringen: kein Schreibvorgang, kein
      // Fehler, und KEIN Rueckfall auf `onPositionAendern` oder den lokalen Weg - der naechste
      // Tastenanschlag mit einem vollstaendigen Wert schreibt regulaer weiter.
      if (("qty" in aenderung && aenderung.qty === "") || ("price" in aenderung && aenderung.price === "")) {
        return;
      }
      const mengePreis: { qty?: number; price?: number } = {};
      if ("qty" in aenderung) mengePreis.qty = Number(aenderung.qty);
      if ("price" in aenderung) mengePreis.price = Number(aenderung.price);
      onMengePreisAendern(weg.jtlPosId, mengePreis);
      return;
    }
    if (weg) {
      onPositionAendern?.(weg.jtlPosId, aenderung as Record<string, unknown>, weg.rowVersion, i);
      return;
    }
    positionenSetzen(positionen.map((p, j) => (j === i ? { ...p, ...aenderung } : p)));
  };

  /**
   * `patchItemFlag` (index.html:9835) - Manual/FOC haben per Definition Warenwert 0.
   *
   * DER PREIS REIST NUR AUF DEM RUECKFALLWEG MIT, und das ist keine Nachlaessigkeit: `price` ist
   * kein tool-eigenes Positionsfeld. Der EK einer Position liegt in JTL
   * (`tLieferantenBestellungPos.fEKNetto`), `POSITIONS_FELDER` kennt ihn deshalb nicht, und
   * `positionAendern` wiese den ganzen Aufruf mit `PositionsfeldNichtSchreibbarFehler` ab - das
   * Kennzeichen ginge mit ihm verloren. Die Null gehoert damit zum JTL-Schreibweg
   * (`jtl/schreiben/aendern.ts`), nicht hierher; im Bestand fielen beide zusammen, weil dort auch
   * der Preis dem Tool gehoerte. GEMELDET, nicht heimlich weggelassen.
   */
  const flagSetzen = (i: number, feld: "manual" | "foc", gesetzt: boolean): void => {
    const aenderung: Partial<OrderItem> = { [feld]: gesetzt } as Partial<OrderItem>;
    if (gesetzt && !schreibwegVon(i)) (aenderung as Record<string, unknown>).price = "0";
    positionPatchen(i, aenderung);
  };

  /** Entfernt NUR das eine Feld (Menge ODER Preis) aus dem Entwurf EINER `jtlPosId` (Nachpruefung
   *  Fix-Runde 2: der stabile Schluessel, nicht der Zeilenindex - Kopfkommentar
   *  `mengePreisEntwuerfe`), laesst das jeweils ANDERE (falls offen) unangetastet stehen -
   *  Hilfsfunktion fuer `entwurfCommitten` unten, das IMMER aufraeumt, ob geschrieben wurde oder
   *  nicht. */
  const entwurfFeldLoeschen = (jtlPosId: number, feld: "qty" | "price"): void => {
    setMengePreisEntwuerfe((e) => {
      const zeile = e[jtlPosId];
      if (!zeile || zeile[feld] === undefined) return e;
      const rest = { ...zeile };
      delete rest[feld];
      const neu = { ...e };
      if (Object.keys(rest).length > 0) neu[jtlPosId] = rest;
      else delete neu[jtlPosId];
      return neu;
    });
  };

  /** Schreibt den getippten Wert eines Feldes NUR in den lokalen Entwurf DIESER `jtlPosId`
   *  (Kopfkommentar `mengePreisEntwuerfe`) - KEIN Netzaufruf, siehe `entwurfCommitten` fuer den
   *  Schreibvorgang. */
  const entwurfAendern = (jtlPosId: number, feld: "qty" | "price", wert: string): void => {
    setMengePreisEntwuerfe((e) => ({ ...e, [jtlPosId]: { ...e[jtlPosId], [feld]: wert } }));
  };

  /**
   * Verlaesst das Feld: loest hoechstens EINEN Schreibvorgang aus (ueber `positionPatchen`, das
   * DIESELBE Menge/Preis-Ableitung wie bisher traegt - I-2-Waechter fuer ein geleertes Feld
   * eingeschlossen, siehe dessen Kopfkommentar). Kein Aufruf bei einem unangetasteten (`undefined`)
   * Feld ODER wenn der Entwurf NUMERISCH mit dem zuletzt bekannten Prop-Wert uebereinstimmt (siehe
   * Kopfkommentar `mengePreisEntwuerfe` fuer den Grund des numerischen statt String-Vergleichs).
   * Raeumt den Entwurf danach IMMER auf, ob geschrieben wurde oder nicht.
   *
   * `i` bleibt der ZEILENINDEX (fuer `positionen[i]`/`positionPatchen`, die beide weiterhin
   * indexbasiert arbeiten), `weg.jtlPosId` ist NUR der Schluessel des Entwurfs - beide gehoeren zum
   * selben Render-Durchlauf derselben Zeile, koennen sich also nie auseinander bewegen (anders als
   * ein ueber mehrere Renderdurchlaeufe hinweg im State gehaltener Index, siehe Kopfkommentar
   * `mengePreisEntwuerfe`).
   */
  const entwurfCommitten = (
    i: number,
    weg: { jtlPosId: number; rowVersion: number },
    feld: "qty" | "price",
  ): void => {
    const entwurf = mengePreisEntwuerfe[weg.jtlPosId]?.[feld];
    entwurfFeldLoeschen(weg.jtlPosId, feld);
    if (entwurf === undefined) return;
    const propZahl = Number((positionen[i]?.[feld] as string | number | undefined) ?? "");
    if (Number(entwurf) === propZahl) return;
    positionPatchen(i, { [feld]: entwurf } as Partial<OrderItem>);
  };

  /**
   * Verhaltensaenderung 3: Tippen sucht, es schreibt nicht in die Position - der Text steht
   * stattdessen im Suchzustand (`Zeilensuche.text`, Fehler 1). `feld` haelt den Bezug fuer die
   * Schwebe-Geometrie der Trefferliste (Fehler 2) fest und wird sofort ausgewertet, damit die Liste
   * an der richtigen Stelle erscheint, noch bevor eine Antwort da ist.
   */
  const suchen = (i: number, begriff: string, feld: HTMLInputElement): void => {
    artikelfeldRef.current = feld;
    geometrieNachfuehren();
    setSuche({ zeile: i, text: begriff, liste: [], fehler: null });

    if (!jtlArtikelSuche) return;
    // MIT Ablehnungszweig (Fehler 3) - `JtlSuchfeld.tsx` hat denselben Befund am 2026-08-13
    // abgestellt: ein `.then(...)` ohne zweiten Zweig macht aus einem Suchfehler eine unbehandelte
    // Zurueckweisung, die der Bediener nie zu sehen bekommt.
    //
    // Die Wache `s.zeile === i && s.text === begriff` verwirft ueberholte Antworten: eine Antwort
    // auf einen laengst ueberholten Suchtext darf nicht mehr in ein inzwischen weitergetipptes Feld
    // schreiben.
    void jtlArtikelSuche(begriff, o.supplierId as string | undefined).then(
      (liste) => setSuche((s) => (s && s.zeile === i && s.text === begriff ? { ...s, liste } : s)),
      (fehler: unknown) =>
        setSuche((s) =>
          s && s.zeile === i && s.text === begriff
            ? {
                ...s,
                liste: [],
                // Inline bleibt inline (Spec R2): der Hinweis steht schon heute an der Suchzeile. Der Text
                // kommt aus dem Uebersetzer (R1) - nie `fehler.message`, das bei Proxy-HTML "HTTP 502" hiesse.
                fehler: fehlertext(fehler, "Suche fehlgeschlagen.") ?? "Suche fehlgeschlagen.",
              }
            : s,
        ),
    );
  };

  /** Verhaltensaenderung 3: der gewaehlte Artikel fuellt Bezeichnung, SKU und EAN. */
  const uebernehmen = (i: number, artikel: JtlArtikel): void => {
    const weg = schreibwegVon(i);
    const kArtikelRoh = Number(artikel.kArtikel);
    const kArtikel = Number.isInteger(kArtikelRoh) && kArtikelRoh > 0 ? kArtikelRoh : null;
    // `artikel.artikelNummer`, NICHT `artikel.sku` - die JTL-Artikelsuche liefert kein `sku`-Feld
    // (`backend/src/jtl/artikel.ts#sucheArtikel`, JtlArtikelTreffer). EAN liefert die Suche gar
    // nicht (weder Spalte noch Typ) - sie wird beim Tausch bewusst geleert, siehe Spec.
    const artikelNummer = (artikel.artikelNummer as string) ?? "";
    const name = (artikel.name as string) ?? "";
    if (weg && kArtikel && onArtikelTauschen) {
      onArtikelTauschen(weg.jtlPosId, { kArtikel, cArtNr: artikelNummer, cName: name || null });
      positionPatchen(i, { ean: "" } as Partial<OrderItem>);
    } else {
      positionPatchen(i, { name, sku: artikelNummer, ean: "" } as Partial<OrderItem>);
      // Task 6: numerischen Kandidaten fuer eine spaetere Anlage merken - nur wenn kein Schreibweg
      // existiert (echte neue Position), sonst waere `weg` gesetzt und der obere Zweig griffe. cName
      // MUSS ein echter, nicht-leerer Text sein (Kopfkommentar `NeueZeile` oben) - ein Treffer ohne
      // (oder nur-Leerzeichen-)Namen hinterlaesst deshalb bewusst KEINEN Kandidaten.
      const cName = name.trim();
      // Pruefer-Befund I-2: `mwstProzent` NICHT still auf 0 zurueckfallen lassen, wenn der Rohwert
      // `null`/`undefined` ist (`sucheArtikel` liefert das bei fehlender Lieferantenverknuepfung) -
      // derselbe Wächter wie bei `cName`: ist der Wert nicht als echte Zahl aus einem NICHT-null
      // Rohwert lesbar, entsteht gar kein Kandidat, statt einer erfundenen "0% Steuer".
      const mwstProzentRoh = (artikel as { mwstProzent?: unknown }).mwstProzent;
      const mwstProzent = mwstProzentRoh != null ? Number(mwstProzentRoh) : NaN;
      // Nachpruefung Runde 2: der Kandidat haengt jetzt DIREKT am Zeilenobjekt in `neueZeilen`
      // (`NeueZeile.kandidat`) - dafuer braucht es zwingend eine ECHTE lokale Zeile
      // (`i >= positionen.length`). Fuer eine props-abgeleitete Zeile ohne Schreibweg (der alte,
      // laute Rueckfall ohne JTL-Kennung) gibt es dafuer keinen Platz mehr - sie bekommt bewusst
      // KEINEN Kandidaten (vorher liess die separate Map das theoretisch zu, ungetestet und ohne
      // Nutzen: `onPositionHinzufuegen` ist fuer eine BESTEHENDE Position ohnehin nie der richtige Weg).
      if (!weg && kArtikel && cName !== "" && i >= positionen.length) {
        const lokalerIndex = i - positionen.length;
        if (Number.isFinite(mwstProzent)) {
          const kandidat = { kArtikel, cArtNr: artikelNummer, cName, mwstProzent };
          // Explizit `steuersatzFehlt: false` mitsetzen (nicht nur `kandidat`) - sonst bliebe eine
          // Warnung aus einer FRUEHEREN Wahl fuer dieselbe Zeile stehen, siehe Kopfkommentar
          // `NeueZeile.steuersatzFehlt`.
          setNeueZeilen((z) =>
            z.map((p, j) => (j === lokalerIndex ? { ...p, kandidat, steuersatzFehlt: false } : p)),
          );
        } else {
          // I3 (Fix-Welle nach der finalen Zweig-Pruefung, 2026-09-04): `kArtikel`/`cName` sind
          // gueltig, NUR der Steuersatz fehlt - VORHER hier stumm KEIN Kandidat, ohne jede sichtbare
          // Rueckmeldung. Jetzt: kein Kandidat (unveraendert, `onPositionHinzufuegen` braucht einen
          // echten Steuersatz), ABER eine sichtbare Marke neben dem Artikelfeld (siehe Render unten).
          setNeueZeilen((z) =>
            z.map((p, j) => (j === lokalerIndex ? { ...p, kandidat: undefined, steuersatzFehlt: true } : p)),
          );
        }
      }
    }
    // NUR lokaler Anzeigezustand (WICHTIGER BEFUND 2) - `verknuepft` reist NIE im `aenderung`-Objekt
    // von `positionPatchen` mit, es ist kein Positionsfeld und `positionAendern` wiese es ab.
    setVerknuepfungen((v) => ({ ...v, [i]: (artikel.verknuepft as boolean | null | undefined) ?? null }));
    setSuche(null);
  };

  /**
   * Die kombinierte Anzeigeliste (Kontroll-Sitzung 2026-09-04, I-3): props-abgeleitete Positionen
   * ZUERST (Index 0..positionen.length-1, unveraendert wie bisher), lokale neue Zeilen DANACH (siehe
   * `neueZeilen` oben). Nur fuer Anzeige und kombinierten Index - `positionen` selbst bleibt ueberall
   * sonst (schreibwegVon, sortIndex, onAendern) unveraendert die reine Prop.
   */
  const alleZeilen = [...positionen, ...neueZeilen];

  return (
    <>
      <div className="items-table-tools">
        <span className="items-sort-hint">
          <Ikone name="arrow-up-down" />
          Positionen am Griff ziehen
        </span>
        <span className="items-resize-hint">Spaltentrenner ziehen</span>
        <button
          className="items-width-reset"
          type="button"
          onClick={() => onSpaltenbreiten?.()}
          title="Artikel-Spaltenbreiten zurücksetzen"
        >
          Zurücksetzen
        </button>
      </div>
      <div className="items-table-scroll">
        <table
          className="items resizable-items"
          id="itemsTable"
          style={{ width: `${itemTableWidth(breiten)}px` }}
        >
          {/* `itemTableColgroup` (index.html:6799). */}
          <colgroup>
            <col style={{ width: `${ITEM_REORDER_COLUMN_WIDTH}px` }} />
            <col style={{ width: `${ITEM_IMAGE_COLUMN_WIDTH}px` }} />
            {ITEM_TABLE_COLUMNS.map((spalte) => (
              <col
                key={spalte.key}
                data-item-column={spalte.key}
                style={{ width: `${itemTableColumnWidth(spalte, breiten)}px` }}
              />
            ))}
            <col style={{ width: `${ITEM_ACTION_COLUMN_WIDTH}px` }} />
          </colgroup>
          <thead>
            <tr>
              <th className="item-reorder-head" aria-label="Reihenfolge"></th>
              <th className="item-image-head" aria-label="Bild"></th>
              {/* `itemTableHeaders` (index.html:6807). */}
              {ITEM_TABLE_COLUMNS.map((spalte) => (
                <th
                  key={spalte.key}
                  className={spalte.numeric ? "num" : ""}
                  data-item-column={spalte.key}
                  title={
                    spalte.key === "type"
                      ? "M = Manual/Box (Warenwert 0) · FOC = kostenlose Überlieferung"
                      : undefined
                  }
                >
                  {spalte.label}
                  <span
                    className="item-column-resizer"
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={`Spalte ${spalte.label} in der Breite ändern`}
                    aria-valuemin={spalte.min}
                    aria-valuemax={spalte.max}
                    aria-valuenow={itemTableColumnWidth(spalte, breiten)}
                    tabIndex={0}
                    title="Spaltenbreite"
                  ></span>
                </th>
              ))}
              <th aria-label="Aktionen"></th>
            </tr>
          </thead>
          <tbody id="itemRows">
            {alleZeilen.map((it, i) => {
              // Einmal je Zeile berechnet - wiederverwendet fuer die Menge/Preis-Felder (Entwurf vs.
              // sofortiger `positionPatchen`), den Loeschknopf und die "neue Position"-Erkennung
              // unten (ersetzt dort das vormals doppelt berechnete `schreibwegVon(i)`).
              const weg = schreibwegVon(i);
              // I3 (Fix-Welle 2026-09-04): nur eine lokale, noch nicht angelegte Zeile kann ueberhaupt
              // `steuersatzFehlt` tragen (siehe Kopfkommentar `NeueZeile.steuersatzFehlt`) - derselbe
              // `i - positionen.length`-Zugriff wie an den uebrigen `neueZeilen[lokalerIndex]`-Stellen.
              const lokalerIndex = i - positionen.length;
              const steuersatzFehlt = lokalerIndex >= 0 ? neueZeilen[lokalerIndex]?.steuersatzFehlt : false;
              const rowSum = (Number(it.qty) || 0) * (Number(it.price) || 0);
              const jtlPosId = positionsSchreibwegAus(positionen, i)?.jtlPosId;
              const produktionsbilderVorhanden = jtlPosId !== undefined && geladeneDokumente(positionsDokumenteZustand).some(
                (dokument) => dokument.jtlPosId === jtlPosId && dokument.docType === "produktionsbilder",
              );
              return (
                <tr
                  // Stabiler Key je Position (Upload-Sperren, Luecke 5): der Zeilenindex wandert bei einem Bestellungs-
                  // wechsel oder dem Loeschen einer Zeile davor, der Zeilen-State (offenes Menue, Fokus) bliebe an der
                  // Nummer haengen. Zeilen ohne `jtlPosId` (lokale neue Zeilen) bekommen einen Key aus eigenem Raum
                  // (`neu-<Index>`), der nie mit `pos-<jtlPosId>` zusammenfaellt.
                  key={jtlPosId !== undefined ? `pos-${jtlPosId}` : `neu-${i}`}
                  className={
                    [
                      it.manual || it.foc ? "item-flagged" : null,
                      verknuepfungen[i] === false ? "item-unlinked" : null,
                    ]
                      .filter(Boolean)
                      .join(" ") || undefined
                  }
                  data-item-scope="order"
                  data-item-index={String(i)}
                >
                  <td className="item-reorder-cell">
                    <button
                      className="item-drag-handle"
                      type="button"
                      aria-label={`Position ${i + 1} verschieben`}
                      title="Position"
                    >
                      <Ikone name="grip-vertical" />
                    </button>
                  </td>
                  <td className="item-image-cell">
                    <Artikelbild kArtikel={it.kArtikel} name={it.name} hatBild={it.hatBild} />
                  </td>
                  <td className="item-article-cell">
                    {/* Verhaltensaenderung 3: Suchfeld statt Freitext (siehe Kopfkommentar). Fehler 1
                        (2026-08-25): waehrend gesucht wird, zeigt das Feld den Suchtext, nicht `it.name`. */}
                    <input
                      className={feldZustandClass({ erwartet: true, leer: !(it.name as string) })}
                      value={suche?.zeile === i ? suche.text : ((it.name as string) ?? "")}
                      placeholder="Artikelname"
                      role="combobox"
                      aria-expanded={Boolean(suche?.zeile === i && suche.liste.length)}
                      aria-autocomplete="list"
                      onChange={(e) => suchen(i, e.currentTarget.value, e.currentTarget)}
                    />
                    {verknuepfungen[i] === false ? (
                      <span title="Nicht mit dem gewählten Lieferanten verknüpft - kann nicht bestellt werden.">
                        {verknuepftMarke(false)}
                      </span>
                    ) : null}
                    {/* I3 (Fix-Welle nach der finalen Zweig-Pruefung, 2026-09-04): sichtbare
                        Rueckmeldung statt einer stummen Sackgasse, wenn der gewaehlte Artikel KEINEN
                        gueltigen Steuersatz beim Lieferanten traegt - derselbe Marken-Mechanismus wie
                        "Nicht verknuepft" oben (eigenes Flag, siehe Kopfkommentar
                        `NeueZeile.steuersatzFehlt` fuer den Grund, warum das NICHT dieselbe Marke ist). */}
                    {steuersatzFehlt ? (
                      <span title="Kein Steuersatz beim Lieferanten hinterlegt - kann so nicht angelegt werden.">
                        <span className="jtl-treffer-marke nicht-verknuepft">Kein Steuersatz</span>
                      </span>
                    ) : null}
                    {suche?.zeile === i && suche.liste.length ? (
                      <div className="jtl-treffer" role="listbox" style={schwebeStil}>
                        {suche.liste.map((artikel, k) => (
                          <button
                            key={`${(artikel.sku as string) ?? k}`}
                            className="jtl-treffer-zeile"
                            type="button"
                            role="option"
                            aria-selected={false}
                            onClick={() => uebernehmen(i, artikel)}
                          >
                            <Artikelbild
                              kArtikel={artikelKennung(artikel)}
                              name={artikel.name}
                              klickbar={false}
                              hatBild={artikel.hatBild}
                            />
                            <span className="jtl-treffer-zeile-inhalt">
                              {(artikel.name as string) ?? ""}
                              <span className="mono">{(artikel.artikelNummer as string) ?? ""}</span>
                            </span>
                            {verknuepftMarke(artikel.verknuepft as boolean | null | undefined)}
                          </button>
                        ))}
                      </div>
                    ) : null}
                    {suche?.zeile === i && suche.fehler ? (
                      <p className="suchfeld-hinweis" role="alert" style={schwebeStil}>
                        {suche.fehler}
                      </p>
                    ) : null}
                  </td>
                  <td>
                    {/* Verhaltensaenderung 3: die SKU kommt aus JTL, nicht aus der Tastatur.
                        Auftrag 8 (strukturell statt Sibling-Element): kein `.feld-lock-anchor`-Span
                        mehr - das Schloss-Symbol kommt seither als CSS `background-image` direkt
                        ueber `.feld-gesperrt` (`styles/neubau/feldzustand.css`), die `<td>` selbst
                        bleibt wie zuvor klassenlos und damit unveraendert gegenueber der in
                        `bestellungTabStruktur.test.tsx` eingefrorenen Baseline. */}
                    <input
                      className={["mono", feldZustandClass({ gesperrt: true })].filter(Boolean).join(" ")}
                      style={{ fontSize: ".78rem" }}
                      value={(it.sku as string) ?? ""}
                      placeholder="SKU"
                      readOnly
                      aria-readonly="true"
                      title="SKU kommt aus der Artikelauswahl und wird hier nicht direkt geändert."
                    />
                  </td>
                  <td className="num">
                    <input
                      className={["qty", feldZustandClass({
                        erwartet: true,
                        leer: it.qty === null || it.qty === undefined || it.qty === "",
                      })]
                        .filter(Boolean)
                        .join(" ")}
                      type="number"
                      min="0"
                      value={
                        weg && onMengePreisAendern
                          ? (mengePreisEntwuerfe[weg.jtlPosId]?.qty ?? ((it.qty as string) ?? ""))
                          : ((it.qty as string) ?? "")
                      }
                      placeholder="0"
                      onInput={(e) => {
                        // Critical-Befund (Kontroll-Sitzung 2026-09-04): MIT Schreibweg schreibt
                        // Tippen NUR noch in den lokalen Entwurf (kein Netzaufruf pro Tastenanschlag
                        // gegen die produktive WaWi) - siehe Kopfkommentar `mengePreisEntwuerfe`.
                        // Ohne Schreibweg (lokale neue Zeile / alter Hub-Rueckfall) unveraendert
                        // sofort ueber `positionPatchen`.
                        if (weg && onMengePreisAendern) {
                          entwurfAendern(weg.jtlPosId, "qty", e.currentTarget.value);
                          return;
                        }
                        positionPatchen(i, { qty: e.currentTarget.value } as Partial<OrderItem>);
                      }}
                      onChange={() => {}}
                      onBlur={() => {
                        // Critical-Befund: MIT Schreibweg committet das Verlassen des Feldes den
                        // Entwurf (hoechstens EIN Schreibvorgang, nur bei tatsaechlicher Aenderung) -
                        // siehe `entwurfCommitten`. Die "neue Position"-Erkennung unten betrifft
                        // ausschliesslich Zeilen OHNE Schreibweg (`!weg`), beide Zweige sind darum
                        // gegenseitig ausschliessend.
                        if (weg && onMengePreisAendern) {
                          entwurfCommitten(i, weg, "qty");
                          return;
                        }
                        // Task 6: eine NEUE Position entsteht erst hier, beim Verlassen des
                        // Mengenfelds - nicht bei jedem Tastendruck (`onInput` oben bleibt
                        // unveraendert der lokale/JTL-Mengenweg fuer BESTEHENDE Positionen).
                        //
                        // Nachpruefung Runde 2: der Kandidat kommt jetzt DIREKT aus dem Zeilenobjekt
                        // in `neueZeilen` (`NeueZeile.kandidat`), nicht mehr aus einer separaten,
                        // index-geschluesselten Map - siehe Kopfkommentar `NeueZeile`. Nur ein Index
                        // jenseits `positionen.length` kann ueberhaupt einen Kandidaten tragen.
                        const lokalerIndex = i - positionen.length;
                        const kandidat = lokalerIndex >= 0 ? neueZeilen[lokalerIndex]?.kandidat : undefined;
                        const qty = Number(it.qty);
                        if (!weg && kandidat && onPositionHinzufuegen && Number.isFinite(qty) && qty > 0) {
                          onPositionHinzufuegen({
                            kArtikel: kandidat.kArtikel,
                            cArtNr: kandidat.cArtNr,
                            cName: kandidat.cName,
                            qty,
                            price: Number(it.price) || 0,
                            mwstProzent: kandidat.mwstProzent,
                          });
                          // Ruling (Kontroll-Sitzung 2026-09-04, Punkt 6): die Zeile ist jetzt
                          // "unterwegs" nach JTL - SOFORT lokal entfernen, noch bevor die
                          // Serverantwort da ist. Bewusster, einfachster Kompromiss dieser Runde: bei
                          // einem spaeteren Fehlschlag verschwindet die Zeile aus der Ansicht (kein
                          // Retry-UX-Ausbau, siehe M-3 im Ledger). Der Kandidat verschwindet
                          // automatisch MIT der Zeile (er haengt jetzt AM Objekt) - kein separater
                          // Aufraeumschritt fuer ihn noetig, und keine Indexverschiebungs-Falle mehr
                          // fuer nachfolgende lokale Zeilen (Nachpruefung Runde 2, kritischer Befund).
                          setNeueZeilen((z) => z.filter((_, j) => j !== lokalerIndex));
                        }
                      }}
                    />
                  </td>
                  <td className="num">
                    <input
                      className={["price", feldZustandClass({
                        erwartet: true,
                        leer: it.price === null || it.price === undefined || it.price === "",
                      })]
                        .filter(Boolean)
                        .join(" ")}
                      type="number"
                      min="0"
                      step="0.001"
                      value={
                        weg && onMengePreisAendern
                          ? (mengePreisEntwuerfe[weg.jtlPosId]?.price ?? ((it.price as string) ?? ""))
                          : ((it.price as string) ?? "")
                      }
                      placeholder="0,00"
                      onInput={(e) => {
                        // Siehe Kommentar am Mengenfeld oben - dasselbe Prinzip fuer den Preis.
                        if (weg && onMengePreisAendern) {
                          entwurfAendern(weg.jtlPosId, "price", e.currentTarget.value);
                          return;
                        }
                        positionPatchen(i, { price: e.currentTarget.value } as Partial<OrderItem>);
                      }}
                      onChange={() => {}}
                      onBlur={() => {
                        if (weg && onMengePreisAendern) entwurfCommitten(i, weg, "price");
                      }}
                    />
                  </td>
                  <td className="row-sum">{rowSum ? fmtMoney(o, rowSum, lieferanten) : "–"}</td>
                  <td className="item-flags">
                    {jtlPosId !== undefined ? (
                      <PositionsdokumentAktionen
                        bestellungId={id}
                        bestellNummer={kennungVon(o, id)}
                        artikelNummer={(it.sku as string) || null}
                        jtlPosId={jtlPosId}
                        dokumenteZustand={positionsDokumenteZustand}
                        dokumenteNeuLaden={positionsDokumenteNeuLaden}
                        offenerSchluessel={offenesPositionsdokumentMenue}
                        onOffenenSchluesselAendern={setOffenesPositionsdokumentMenue}
                        onProduktionsbildOeffnen={onProduktionsbildOeffnen}
                        onStehenderHinweis={onStehenderHinweis}
                        uploadStellen={uploadStellen}
                      />
                    ) : null}
                    {/* Das Badge behauptet „vorhanden“ bzw. „nicht vorhanden“ und oeffnet das Menue: ohne geladenen Stand weder noch (Task 13, E4). */}
                    {jtlPosId !== undefined && positionsDokumenteZustand.art === "geladen" ? (
                      <button
                        type="button"
                        className="item-document-badge"
                        data-rollenhinweis-bei-sperre
                        aria-label={`Produktionsbilder für Position ${i + 1} öffnen`}
                        title={
                          produktionsbilderVorhanden ? "Produktionsbilder vorhanden" : "Produktionsbilder nicht vorhanden"
                        }
                        onClick={() => {
                          if (onProduktionsbilderOeffnen) {
                            onProduktionsbilderOeffnen(jtlPosId);
                            return;
                          }
                          setOffenesPositionsdokumentMenue({ jtlPosId, docType: "produktionsbilder" });
                        }}
                      >
                        <Ikone name="image" />
                        <Ikone name={produktionsbilderVorhanden ? "circle-check" : "circle"} />
                      </button>
                    ) : null}
                    <label
                      className="iflag"
                      title="Manual / Box – Bedienungsanleitung (EN) oder Verpackung, Warenwert 0"
                    >
                      <input
                        type="checkbox"
                        checked={!!it.manual}
                        onChange={(e) => flagSetzen(i, "manual", e.currentTarget.checked)}
                      />
                      <span>M</span>
                    </label>
                    <label
                      className="iflag foc"
                      title="Kostenlose Überlieferung (FOC) – senkt den durchschnittlichen EK"
                    >
                      <input
                        type="checkbox"
                        checked={!!it.foc}
                        onChange={(e) => flagSetzen(i, "foc", e.currentTarget.checked)}
                      />
                      <span>FOC</span>
                    </label>
                  </td>
                  <td>
                    <button
                      className="row-del"
                      title="Zeile löschen"
                      onClick={() => {
                        // Das Loeschen verschiebt Indizes - eine offene Suche hinge sonst mitsamt
                        // ihrem Text an der falschen Zeile (derselbe Grund wie
                        // `PositionsEditor#entfernen`). Derselbe Grund gilt fuer den lokalen
                        // Verknuepfungsstand (WICHTIGER BEFUND 2): kompletter Reset statt Umsortieren.
                        // Pruefer-Befund I-3: GILT FUER BEIDE Loeschwege, nicht nur den lokalen - auch
                        // wenn `onPositionLoeschen` die Zeile (noch) nicht aus `positionen` entfernt
                        // (das macht Task 7/App.tsx erst nach der Serverantwort), muss eine an einer
                        // ANDEREN Zeile offene Suche/Marke trotzdem verschwinden, sobald sich die Liste
                        // gleich sortiert veraendern koennte.
                        setSuche(null);
                        setVerknuepfungen({});
                        // Kontroll-Sitzung 2026-09-04 (I-1): `neueZeilen` haengt genauso am
                        // kombinierten Index wie `verknuepfungen` - JEDE Loeschung kann Indizes
                        // verschieben (auch die einer props-abgeleiteten Zeile verschiebt alle
                        // `neueZeilen` im kombinierten Index nach vorn). Einfachster sicherer
                        // Kompromiss (Ruling): der State wird komplett zurueckgesetzt statt
                        // umsortiert - eine halbfertige neue Zeile (samt ihrem am Objekt haengenden
                        // Kandidaten, siehe `NeueZeile`) geht dabei mit verloren, dafuer kann ein
                        // Kandidat nie an der falschen Zeile landen.
                        setNeueZeilen([]);
                        // Fix-Runde 2026-09-04, Runde 2: `mengePreisEntwuerfe` braucht HIER (anders
                        // als `verknuepfungen`/`neueZeilen`) KEINEN Reset mehr - seit der
                        // Nachpruefung ueber die stabile `jtlPosId` geschluesselt, nicht mehr ueber
                        // den Zeilenindex. Siehe Kopfkommentar `mengePreisEntwuerfe`.
                        if (i >= positionen.length) {
                          // Eine rein lokale, nie in JTL angelegte Zeile - der Reset oben hat sie
                          // bereits entfernt, kein `onAendern`/Server-Aufruf noetig (I-3, Punkt 4).
                          return;
                        }
                        // Task 5: bei vorhandenem Schreibweg loescht `onPositionLoeschen` die
                        // Position ueber den positions-scoped JTL-Endpunkt statt sie hier lokal aus
                        // der Liste zu streichen - der Aufrufer (Task 7/App.tsx) entfernt sie erst
                        // nach erfolgreichem Server-Aufruf aus der Bestellung. `weg` (oben, einmal je
                        // Zeile berechnet) statt einer erneuten `schreibwegVon(i)`-Berechnung hier.
                        if (weg && onPositionLoeschen) {
                          onPositionLoeschen(weg.jtlPosId);
                          return;
                        }
                        positionenSetzen(positionen.filter((_, j) => j !== i));
                      }}
                    >
                      <Ikone name="trash-2" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="items-legend">
        M = Manual/Box (Warenwert 0, EN) · FOC = kostenlose Überlieferung (senkt den Ø-EK)
      </div>
      {/* Asana „Reiter Bestellung - Positionsanzahl abbilden" (2026-10-20): wie die JTL-Spalte
          „Anzahl Positionen" - alle in JTL GESPEICHERTEN Positionen inkl. Freipositionen. Quelle ist
          `positionen` (= `o.items`), NICHT `alleZeilen`: die noch ungespeicherten `neueZeilen` zaehlen
          nicht mit. Auch bei 0 sichtbar. */}
      <div className="items-count" data-testid="positionen-anzahl">
        <span className="label">Anzahl Positionen:</span> <span className="value">{positionen.length}</span>
      </div>
      <button
        className="btn-ghost"
        // Kontroll-Sitzung 2026-09-04 (I-3): NICHT mehr ueber `positionenSetzen`/`onAendern` - siehe
        // Kopfkommentar `positionenSetzen` oben. Die neue Zeile entsteht rein lokal.
        onClick={() => setNeueZeilen((z) => [...z, blankOrderItem()])}
      >
        <Ikone name="circle-plus" />
        Artikel hinzufügen
      </button>
      <div className="items-total">
        <span className="label">Warenwert (Positionen)</span>
        {/* `renderItems` setzt `#grandTotal` nach dem Zeichnen (index.html:9817). */}
        <span className="value" id="grandTotal">
          {fmtMoney(o, orderTotal(o), lieferanten)}
        </span>
      </div>
    </>
  );
}
